import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import classNames from 'classnames';

import IDatatableEntry from 'common/types/IDatatableEntry';

import Formatters from 'common/formatters';

import './data-table.scss';
import iconsIndex from 'assets/images/Icons/iconsIndex';
import SortIcon from './SortIcon';
import Loading from 'components/Loading';

interface DataTableProps {
  checkedIds?: Array<number>
  content: Array<Array<string|number|IDatatableEntry|null>>
  headers: Array<string>
  isLoading: boolean
  setCheckedIds?: Dispatch<SetStateAction<number[]>>
  sortFunction?: (key: number) => void
  sortable?: boolean
  sortBy?: {headerKey: number, asc: boolean}
  withCheckboxes?: boolean
}

const DataTable = ({
  checkedIds,
  content,
  headers,
  isLoading,
  setCheckedIds,
  sortable = false,
  sortBy = {
    asc: false,
    headerKey: 0,
  },
  sortFunction,
  withCheckboxes = false,
}: DataTableProps) => {
  const [ids, setIds] = useState<Array<number>>([]);
  const animationRef = useRef(null);

  useEffect(() => {
    if(withCheckboxes) {
      setIds(content.map((c) => c[0] as number));
    }
  }, [content]);

  const toggleCheckboxes = (e: ChangeEvent<HTMLInputElement>) => {
    setCheckedIds!(e.target.checked ? ids : []);
  };

  const toggleId = (id:number) => {
    setCheckedIds!((old) => old.includes(id) ? old.filter((oldId) => oldId !== id) : [...old, id]);
  };

  const sort = (headerKey: number) => {
    if(sortable) {
     sortFunction!(headerKey);
    }
  };

  const getSortArrows = (key: number): {
    name: keyof typeof iconsIndex,
    className: string|undefined
  } => ({
    className: classNames({
      down: sortBy.asc || sortBy.headerKey !== key,
      lit: sortBy.headerKey === key,
    }),
    name: sortBy.headerKey === key ? 'ArrowLong' : 'ArrowUpDown',
  });

  return (
    <div className='data-table-scroll'>
      <table className='data-table'>
        <thead>
          <tr className={classNames({ sortable })}>
            {withCheckboxes && (
              <th className='checkbox'>
                <input
                  type='checkbox'
                  onChange={toggleCheckboxes}
                  checked={ids.length === checkedIds!.length && 0 < ids.length}
                />
              </th>
            )}
            {headers.map((value, key) => (
              <th
                key={key}
                onClick={() => {
                  sort(key);
                }}
              >
                {value} {sortable && (
                  <SortIcon headerKey={sortBy.headerKey} {...getSortArrows(key)} />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <SwitchTransition>
          <CSSTransition
            classNames='page-transition'
            timeout={0}
            nodeRef={animationRef}
            key={content.toString()}
          >
            <tbody ref={animationRef} >
              {isLoading ? (
                <tr>
                  <td colSpan={headers.length}><Loading /></td>
                </tr>
              ) : content.map((entry, key) => (
                <tr key={key}>
                  {withCheckboxes && (
                    <td className='checkbox'>
                      <input type='checkbox' checked={checkedIds!.includes(entry[0] as number)} onChange={() => {
                        toggleId(entry[0] as number);
                      }} />
                    </td>
                  )}
                  {(withCheckboxes ? entry.slice(1) : entry).map((item, k) => (
                    <td key={k}>{
                      'object' === typeof item ? <Formatters {...item as IDatatableEntry} /> : item
                    }</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </CSSTransition>
        </SwitchTransition>
      </table>
    </div>
  );
};

export default DataTable;